import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { SubmitButton } from "common/components/forms/SubmitButton";
import { Loading } from "common/components/Loading";
import { H5 } from "common/components/Typography";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { useMyExamSyllabus } from "common/hooks/useMyExamSyllabus";
import { contentToSelectOption } from "domain/admin/util";
import { isPublished } from "domain/common/utils";
import styles from "domain/front-page/FrontPage.module.css";
import gql from "graphql-tag";
import React from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import {
  GetMyStudyProgrammes_studyProgrammeList,
  GetStudyProgrammeExamSyllabi,
  GetStudyProgrammeExamSyllabiVariables,
} from "__generated__/types";

const GET_STUDY_PROGRAMME_EXAMS = gql`
  query GetStudyProgrammeExamSyllabi($studyProgrammeId: ID!) {
    studyProgrammeById(id: $studyProgrammeId) {
      title
      id
      examSyllabusElements: elements(elementType: EXAM_SYLLABUS_ELEMENT) {
        content {
          id
          title
          status
        }
      }
      status
    }
  }
`;

interface FormValues {
  studyProgrammeId: string;
  examSyllabus: { value: string; label: string }[];
}

interface Props {
  studyProgramme: GetMyStudyProgrammes_studyProgrammeList;
}

export const SelectExamSyllabus: React.FC<React.PropsWithChildren<Props>> = ({
  studyProgramme,
}) => {
  const { data, loadingOrError } = useSimpleQuery<
    GetStudyProgrammeExamSyllabi,
    GetStudyProgrammeExamSyllabiVariables
  >(GET_STUDY_PROGRAMME_EXAMS, {
    variables: {
      studyProgrammeId: studyProgramme.id,
    },
  });
  const methods = useForm<FormValues>({
    defaultValues: {
      studyProgrammeId: studyProgramme.id,
    },
  });

  const { myExamSyllabi, saveExamSyllabiForUser, signUpLoading, signUpError } =
    useMyExamSyllabus();

  if (
    !data ||
    !data.studyProgrammeById.examSyllabusElements ||
    !myExamSyllabi
  ) {
    return <Loading>{loadingOrError}</Loading>;
  }

  const examsInStudyProgramme =
    data.studyProgrammeById.examSyllabusElements.map((e) => e.content);

  return (
    <div style={{ margin: "1rem" }}>
      <H5 withSpacing={false}>{studyProgramme.title}</H5>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(saveExamSyllabiForUser)}>
          <div className={styles.formContainer}>
            <Controller
              name="examSyllabus"
              control={methods.control}
              render={({ field }) => (
                <ReactSelect
                  options={examsInStudyProgramme
                    .filter(isPublished)
                    .map(contentToSelectOption)}
                  noOptionsMessage={() =>
                    "Vi fant dessverre ikke pensum å vise!"
                  }
                  placeholder="Velg pensum"
                  isMulti={true}
                  defaultValue={myExamSyllabi
                    .filter((exams) =>
                      examsInStudyProgramme.map((e) => e.id).includes(exams.id),
                    )
                    .map(contentToSelectOption)}
                  styles={{
                    option: (provided) => ({
                      ...provided,
                      color: "black",
                    }),
                  }}
                  {...field}
                />
              )}
            />
            <SubmitButton kind="secondary" loading={signUpLoading}>
              Lagre
            </SubmitButton>
          </div>
          <MutationLoadingOrError loading={signUpLoading} error={signUpError} />
        </form>
      </FormProvider>
    </div>
  );
};
